import React, { useCallback, useEffect } from "react";

import styled from "styled-components/macro";

import { useFamilyRegistrationContext } from "Signups/FamilyRegistration/FamilyRegistrationContext";
import CheckboxSettingSection from "Signups/FamilyRegistration/Shared/CheckboxSettingSection";
import ErrorMessage from "Signups/FamilyRegistration/Shared/ErrorMessage";
import useFamilyRegistrationAdminPermissions from "Signups/FamilyRegistration/Shared/useFamilyRegistrationAdminPermissions";
import useUpdateSetting from "Signups/FamilyRegistration/Shared/useUpdateSetting";

import FnrLoadingBricks from "UI/FnrLoadingBricks";
import FnrTextEditor from "UI/FnrTextEditor";

/**
 * KNOWN BUG
 * If someone tries to paste something way over the 255 character limit,
 * nothing happens. It doesn't paste anything or trigger any events.
 * If they paste something just over the limit, then it pastes to the limit for some reason.
 * This is all related to the tiptap editor and the CharacterCount extension.
 * Explore the onTransaction function when intantiating the editor.
 * There may be a way to recognize a paste event that pastes too much
 * and pass some sort of error message back to the user.
 */

export default function ClosingMessage() {
  const { state, dispatch } = useFamilyRegistrationContext();
  const { updateSetting, saving, error, setError } = useUpdateSetting();

  const text = state.textboxes.closingMessage || "";

  const setText = useCallback(
    (text) => {
      dispatch({ type: "setClosingMessage", payload: text });
    },
    [dispatch]
  );

  const { shouldDisableFamilyRegistrationSettings } =
    useFamilyRegistrationAdminPermissions();

  const message = state?.familyRegistrationSettings?.closingMessage?.message;

  const handleChange = (newText) => {
    setText(newText);

    const onlyText = newText.replace(/(<([^>]+)>)/gi, "");
    const maxLengthReached = onlyText.length >= 255;
    if (maxLengthReached) {
      setError({
        error: "You've reach the maximum length of the closing message.",
      });
    } else {
      setError(null);
    }
  };

  const saveOnBlur = () => {
    if (message === text) return;

    setTimeout(() => {
      // tiptap blurs when clicking the format buttons, then refocuses on the text editor
      // this only saves when it truly loses focus
      let activeEl = document.activeElement;
      let activeElIsTextEditor =
        activeEl.classList.value.includes("ProseMirror");

      if (activeElIsTextEditor) return;

      updateSetting({
        setting: "closingMessage",
        value: text,
      });
    }, 200);
  };

  useEffect(
    function setSavedMessage() {
      if (message) setText(message);
    },
    [message, setText]
  );
  return (
    <CheckboxSettingSection
      setting="closingMessage"
      label="Add a closing message"
      disabled={shouldDisableFamilyRegistrationSettings}
    >
      <div
        style={{ maxWidth: "520px", position: "relative" }}
        onBlur={saveOnBlur}
      >
        <div style={{ minHeight: "100px" }}>
          <FnrTextEditor
            content={text.length === 0 && message ? message : text}
            setContent={handleChange}
            maxLength={255}
            minHeight="100px"
          />
        </div>

        {saving ? (
          <LoadingWrapper>
            <FnrLoadingBricks show={saving} />
          </LoadingWrapper>
        ) : null}

        {error ? (
          <ErrorMessage
            error={error?.error}
            marginLeft="0px"
            maxWidth="520px"
          />
        ) : null}
      </div>
    </CheckboxSettingSection>
  );
}

const LoadingWrapper = styled.div`
  z-index: 1000;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  place-items: center;
  background-color: hsl(0 0% 100% / 0.5);
`;
